import React, { useEffect, useState } from 'react';
import { Button } from '@naf-simba/elements-buttons';
import { Input, Message } from '@naf/input';
import { CheckCircle } from '@styled-icons/material/CheckCircle';
import { useDispatch } from 'react-redux';
import useSelector from '@redux/typedHooks';
import { useQueryParams } from '@hooks/useQueryParameters';
import { updateUrlQueryValues } from '@utils/updateUrlQueryValues';
import { actionCreators } from '@redux/modules/application';
import { actions as becomeAMemberActions } from '@redux/modules/becomeAMember';
import SimpleTextLink from '@components/simple-textlink/SimpleTextLink';
import * as S from './CampaignCodeInput.styles';

export const CampaignCodeInput = () => {
  const dispatch = useDispatch();
  const queryParameters = useQueryParams();
  const campaignCodeDetails = useSelector((state) => state.becomeAMember?.data?.campaignCodeDetails);
  const [temporaryCampaignCode, setTemporaryCampaignCode] = useState<string | undefined>(queryParameters?.campaignCode);

  useEffect(() => {
    if (queryParameters?.campaignCode) {
      setTemporaryCampaignCode(queryParameters?.campaignCode);
    }
  }, [queryParameters?.campaignCode]);

  if (!campaignCodeDetails?.hasAvailableCampaignCodes) {
    return null;
  }

  const handleToggleCampaignCode = () => {
    if (typeof queryParameters?.campaignCode === 'undefined') {
      updateUrlQueryValues.updateCampaignCode('');
    } else {
      updateUrlQueryValues.removeCampaignCode();
      setTemporaryCampaignCode(undefined);
      dispatch(becomeAMemberActions.getBecomeAMember.request(undefined));
    }
    dispatch(actionCreators.incrementUrlParamsIndex());
  };

  const handleApplyCampaignCode = () => {
    updateUrlQueryValues.updateCampaignCode(temporaryCampaignCode);
    dispatch(becomeAMemberActions.getBecomeAMember.request(temporaryCampaignCode));
  };

  const handleRemoveCampaignCode = () => {
    updateUrlQueryValues.removeCampaignCode();
    setTemporaryCampaignCode(undefined);
    dispatch(becomeAMemberActions.getBecomeAMember.request(undefined));
    dispatch(actionCreators.incrementUrlParamsIndex());
  };

  return (
    <S.Label htmlFor="campaignCode">
      <S.LabelTextWrapper $isHidden={campaignCodeDetails?.isValid}>
        <S.LabelIcon $visible={typeof queryParameters?.campaignCode !== 'undefined'}>-</S.LabelIcon>
        <S.LabelIcon $visible={typeof queryParameters?.campaignCode === 'undefined'}>+</S.LabelIcon>
        <S.LabelText onClick={handleToggleCampaignCode}>Legg til kampanjekode</S.LabelText>
        <S.TooltipWrapper>
          <S.StyledTooltip title="Har du mottatt en kampanjekode fra NAF kan du skrive inn den her. Kampanjekoder gir ikke rabatt på medlemskontingent." />
        </S.TooltipWrapper>
      </S.LabelTextWrapper>
      <S.CampaignCode $visible={typeof queryParameters?.campaignCode !== 'undefined'}>
        <S.InputWrapper $isHidden={campaignCodeDetails?.isValid}>
          <Input
            error={campaignCodeDetails?.isValid === false}
            onChange={(e: string) => {
              setTemporaryCampaignCode(e);
            }}
            value={temporaryCampaignCode || queryParameters?.campaignCode}
            placeholder="Kampanjekode"
          />
          <Button
            size="medium"
            textWrap="nowrap"
            disabled={campaignCodeDetails?.isValid === true}
            onClick={handleApplyCampaignCode}
          >
            {campaignCodeDetails?.isValid === true ? 'Lagt til' : 'Legg til'}
          </Button>
        </S.InputWrapper>
        {campaignCodeDetails?.isValid === true && (
          <S.CampaignCodeMessageWrapper>
            <CheckCircle size={24} />
            <div>
              <S.CampaignCodeMessageText>
                Kampanjekoden er lagt til ({campaignCodeDetails?.campaignCode})
              </S.CampaignCodeMessageText>
              <S.CampaignCodeMessageText>{campaignCodeDetails?.description}</S.CampaignCodeMessageText>
              <SimpleTextLink onClick={handleRemoveCampaignCode}>Fjern kode</SimpleTextLink>
            </div>
          </S.CampaignCodeMessageWrapper>
        )}
        {campaignCodeDetails?.isValid === false && <Message error>Kampanjekoden er ikke gyldig!</Message>}
      </S.CampaignCode>
    </S.Label>
  );
};
